/* Sprint 2L — Simple Driver PWA — mobile-first start/break/end route flow. */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Play, Pause, Square, Truck, MapPin, Package, Clock, AlertTriangle, Map } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function driverApi() {
  const token = localStorage.getItem("ema_driver_token");
  return axios.create({ baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {} });
}

async function getGPS() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { timeout: 5000, enableHighAccuracy: false },
    );
  });
}

export default function DriverMyRoutes() {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmRoute, setConfirmRoute] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await driverApi().get("/driver/my-routes");
      setRoutes(data.items || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        window.location.href = "/driver/login";
        return;
      }
      toast.error("Kunne ikke hente ruter");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startRoute = async (route_id) => {
    const gps = await getGPS();
    try {
      await driverApi().post(`/driver/routes/${route_id}/start`, gps ? { gps_lat: gps.lat, gps_lng: gps.lng } : {});
      toast.success("Rute startet");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Start fejlede"); }
  };

  const startBreak = async (route_id) => {
    try {
      await driverApi().post(`/driver/routes/${route_id}/break/start`, { paid: false, included_in_route_time: false });
      toast.success("Pause startet");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Pause fejlede"); }
  };

  const endBreak = async (route_id) => {
    try {
      await driverApi().post(`/driver/routes/${route_id}/break/end`, {});
      toast.success("Pause afsluttet");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Pause-slut fejlede"); }
  };

  if (loading) return (
    <div className="min-h-screen bg-brand-bg flex items-center justify-center">
      <Loader2 className="animate-spin text-brand-red" size={32}/>
    </div>
  );

  return (
    <div className="min-h-screen bg-brand-bg pb-20" data-testid="driver-my-routes">
      <div className="bg-brand-dark text-white px-4 py-4 sticky top-0 z-10 flex items-center justify-between">
        <div>
          <div className="font-display font-bold text-lg">Mine ruter</div>
          <div className="text-xs text-white/60">EMA Kurer · Driver PWA</div>
        </div>
        <div className="flex items-center gap-2">
          <a href="/driver/cages" data-testid="open-cages"
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 text-xs font-bold tracking-wider uppercase flex items-center gap-1">
            Hent bur
          </a>
          <a href="/driver/my-performance" data-testid="open-my-performance"
            className="bg-brand-bg2 text-brand-dark px-3 py-2 text-xs font-bold tracking-wider uppercase flex items-center gap-1">
            Min performance
          </a>
          <a href="/driver/vagtplan" data-testid="open-vagtplan"
            className="bg-brand-red hover:bg-brand-red/90 text-white px-3 py-2 text-xs font-bold tracking-wider uppercase flex items-center gap-1">
            Vagtplan
          </a>
        </div>
      </div>
      <div className="p-4 space-y-3">
        {routes.length === 0 ? (
          <div className="bg-white border border-brand p-6 text-center text-brand-text2">
            Ingen ruter tildelt de seneste 3 dage.
          </div>
        ) : routes.map((r) => (
          <div key={r.route_id} className="bg-white border border-brand p-4 space-y-2" data-testid={`driver-route-${r.route_id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-display font-bold text-base text-brand-dark">{r.route_number || r.route_id.slice(0, 8)}</div>
                <div className="text-xs text-brand-text2">{r.route_date} · {r.route_type}</div>
              </div>
              <span className={`px-2 py-1 text-xs font-semibold ${r.shift_status === "completed" ? "bg-green-100 text-green-800" : r.shift_status === "started" ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-800"}`}>{r.shift_status}</span>
            </div>
            <div className="flex items-center gap-4 text-sm text-brand-text2 flex-wrap">
              <span className="flex items-center gap-1"><MapPin size={14}/> {r.pickup_location}</span>
              <span className="flex items-center gap-1"><Package size={14}/> {r.total_packages} pakker</span>
              {typeof r.delivered_packages === "number" && (
                <span className="flex items-center gap-1 text-green-700"><Package size={14}/> {r.delivered_packages} leveret</span>
              )}
              {r.undelivered_packages > 0 && (
                <span className="flex items-center gap-1 text-red-700 font-semibold" data-testid={`driver-undelivered-count-${r.route_id}`}>
                  <AlertTriangle size={14}/> {r.undelivered_packages} ikke leveret
                </span>
              )}
            </div>
            {r.undelivered_list && r.undelivered_list.length > 0 && (
              <div className="bg-red-50 border border-red-200 p-2 text-xs space-y-1" data-testid={`driver-undelivered-list-${r.route_id}`}>
                <div className="font-bold text-red-900">Pakker som skal returneres:</div>
                {r.undelivered_list.map((p) => (
                  <div key={p.id} className="text-red-900">
                    • {p.label_number} — {p.postal_code} {p.city} <span className="text-red-600">({p.reason})</span>
                  </div>
                ))}
              </div>
            )}
            {r.shift_started && <div className="text-xs text-brand-text2"><Clock size={12} className="inline mr-1"/> Startet {new Date(r.shift_started).toLocaleTimeString("da-DK")}</div>}
            {r.shift_ended && <div className="text-xs text-brand-text2">Sluttet {new Date(r.shift_ended).toLocaleTimeString("da-DK")}</div>}

            <div className="flex flex-wrap gap-2 pt-2 border-t border-brand">
              <a
                href={`/driver/routes/${r.route_id}/map`}
                className="flex-1 min-w-[140px] px-4 py-3 bg-brand-dark text-white font-bold flex items-center justify-center gap-2 uppercase tracking-wider text-sm"
                data-testid={`driver-open-map-${r.route_id}`}
              >
                <Map size={18}/> Kort & navigation
              </a>
              {!r.shift_started && (
                <button onClick={() => startRoute(r.route_id)} className="flex-1 px-4 py-3 bg-brand-red text-white font-bold flex items-center justify-center gap-2" data-testid={`driver-start-${r.route_id}`}>
                  <Play size={18}/> Start rute
                </button>
              )}
              {r.shift_started && !r.shift_ended && (
                <>
                  <button onClick={() => startBreak(r.route_id)} className="flex-1 px-4 py-3 bg-amber-500 text-white font-bold flex items-center justify-center gap-2" data-testid={`driver-break-start-${r.route_id}`}>
                    <Pause size={18}/> Start pause
                  </button>
                  <button onClick={() => endBreak(r.route_id)} className="flex-1 px-4 py-3 bg-blue-500 text-white font-bold flex items-center justify-center gap-2" data-testid={`driver-break-end-${r.route_id}`}>
                    <Play size={18}/> Slut pause
                  </button>
                  <button onClick={() => setConfirmRoute(r)} className="w-full px-4 py-3 bg-brand-dark text-white font-bold flex items-center justify-center gap-2" data-testid={`driver-end-${r.route_id}`}>
                    <Square size={18}/> Slut rute
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {confirmRoute && <EndRouteConfirmation route={confirmRoute} onClose={() => setConfirmRoute(null)} onDone={() => { setConfirmRoute(null); load(); }}/>}
    </div>
  );
}

function EndRouteConfirmation({ route, onClose, onDone }) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [comment, setComment] = useState("");
  const [skippedBreakReason, setSkippedBreakReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!acknowledged) { toast.error("Du skal bekræfte returreglerne"); return; }
    setBusy(true);
    try {
      const gps = await getGPS();
      await driverApi().post(`/driver/routes/${route.route_id}/confirm-completion`, {
        acknowledgement: true,
        comment: comment || null,
        skipped_break_reason: skippedBreakReason || null,
        gps_lat: gps?.lat, gps_lng: gps?.lng,
      });
      toast.success("Rute afsluttet — tak for turen!");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Afslutning fejlede"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-end md:items-center justify-center" data-testid="end-route-modal">
      <div className="bg-white w-full md:max-w-lg md:border md:border-brand max-h-[92vh] overflow-y-auto">
        <div className="bg-brand-dark text-white px-4 py-3 flex items-center justify-between sticky top-0">
          <div className="font-display font-bold">Afslut rute</div>
          <button onClick={onClose} className="text-white/80 hover:text-white text-2xl leading-none">×</button>
        </div>
        <div className="p-4 space-y-4">
          <div className="bg-brand-bg2 border border-brand p-3 text-sm space-y-1">
            <div><b>Rute:</b> {route.route_number || route.route_id.slice(0, 8)}</div>
            <div><b>Pakker i alt:</b> {route.total_packages}</div>
          </div>

          <div className="border-l-4 border-amber-500 bg-amber-50 p-3 text-sm">
            <p className="font-bold mb-1 flex items-center gap-1 text-amber-900"><AlertTriangle size={16}/> Returregler</p>
            <p className="text-amber-900 mb-2">
              <b>Normal fejlleverance:</b> Pakker som ikke kunne leveres, skal returneres til EMA Kurer/hub/pickup location <b>senest 2 hverdage</b> efter rutedato, medmindre du har fået anden instruks fra EMA Kurer.
            </p>
            <p className="text-amber-900">
              <b>Afbrudt rute:</b> Hvis du afslutter turen uden at færdiggøre alle leveringer, skal alle resterende pakker returneres <b>samme dag</b>. Pakker må ikke opbevares privat eller blive liggende i bilen uden aftale med EMA Kurer.
            </p>
          </div>

          <label className="flex items-start gap-2 text-sm" data-testid="end-route-ack-label">
            <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)}
                   className="mt-1" data-testid="end-route-ack"/>
            <span>Jeg bekræfter, at ruten er afsluttet, og at jeg har læst returreglerne for ikke-leverede pakker.</span>
          </label>

          <div>
            <label className="text-xs text-brand-text2">Pause sprunget over? (valgfri begrundelse)</label>
            <input className="input-flat" value={skippedBreakReason} onChange={(e) => setSkippedBreakReason(e.target.value)} placeholder="F.eks. korte stops, ingen tid" data-testid="end-route-skipped-break"/>
          </div>

          <div>
            <label className="text-xs text-brand-text2">Kommentar (valgfri)</label>
            <textarea className="input-flat" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Bemærkninger til ruten" data-testid="end-route-comment"/>
          </div>

          <div className="flex gap-2 pt-2 border-t border-brand">
            <button onClick={onClose} className="flex-1 px-4 py-3 bg-brand-bg2 border border-brand text-brand-text2 font-semibold">Annullér</button>
            <button onClick={submit} disabled={!acknowledged || busy} className="flex-1 px-4 py-3 bg-brand-red text-white font-bold disabled:opacity-50" data-testid="end-route-submit">
              {busy ? <><Loader2 className="inline animate-spin mr-1" size={14}/> Sender...</> : "Bekræft og afslut"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
